import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../../styles/newhome.css';
import '../../styles/landing-fonts.css';
import '../../Landing organization/uploads/mimt/assets/css/sm-core-css.css';
import '../../Landing organization/uploads/mimt/assets/css/sm-simple.css';
import logoImage from '../../Landing organization/uploads/mimt/assets/images/logo.webp';
import NavContext from '../../contexts/NavContext';
import { getCategoryHierarchy } from '../../services/postService';

const LABELS = {
  home: 'صفحه اصلی',
  auction: 'مزایده و مناقصه',
  projects: 'پروژه ها',
  news: 'اخبار',
  company: 'معرفی شرکت',
  contactUs: 'تماس با ما',
  reservation: 'رزرواسیون',
  about: 'آشنایی با ما',
};

const TEXTS = {
  brand: 'مهر ۷۸',
  logoAlt: 'لوگو',
  openMenu: 'باز کردن منو',
  close: 'بستن',
  searchHint: 'برای جستجو تایپ کرده و Enter را بزنید',
  searchPlaceholder: 'جستجو',
};

const STATIC_MENU_ITEMS = [
  {
    label: LABELS.about || 'درباره مجموعه',
    children: [
      { label: LABELS.company || 'معرفی شرکت', to: '/company-introduction' },
      { label:  'مدیران ارشد و سازمانی', to: '/chart' },
      { label: LABELS.contactUs || 'تماس با ما', to: '/contact-us' },
    ],
  },
];

// کامپوننت منوی بازگشتی برای دسکتاپ
const DesktopMenuItem = ({ item, level = 0, activePath, openKeys, toggleOpen }) => {
  const hasChildren = item.children && item.children.length > 0;
  const itemKey = item.id ? `item-${item.id}` : `static-${item.label}`;
  const isOpen = openKeys.includes(itemKey);
  const isExternal = item.to && (item.to.startsWith('http://') || item.to.startsWith('https://'));
  
  // بررسی فعال بودن آیتم
  const isActive = () => {
    if (item.to) {
      if (item.to === '/') return activePath === '/';
      return activePath.startsWith(item.to);
    }
    return false;
  };

  const handleItemClick = (e) => {
    if (hasChildren) {
      e.preventDefault();
      toggleOpen(itemKey);
    }
  };

  const renderLink = () => {
    if (!item.to) {
      return (
        <a href="#" onClick={handleItemClick}>
          {item.label}
          {hasChildren && <i className="fa-solid fa-angle-down sub-caret" />}
        </a>
      );
    }
    
    if (isExternal) {
      return (
        <a href={item.to} target="_blank" rel="noopener noreferrer">
          {item.label}
        </a>
      );
    }
    
    return (
      <Link to={item.to} onClick={() => hasChildren && handleItemClick}>
        {item.label}
        {hasChildren && <i className="fa-solid fa-angle-left sub-caret" />}
      </Link>
    );
  };

  return (
    <li className={`${hasChildren ? 'has-sub' : ''} ${isActive() ? 'active' : ''} ${isOpen ? 'open' : ''}`}>
      {renderLink()}
      
      {hasChildren && (
        <ul className={`sub-menu level-${level + 1}`}>
          {item.children.map((child) => (
            <DesktopMenuItem
              key={child.id ? `item-${child.id}` : `child-${child.label}`}
              item={child}
              level={level + 1}
              activePath={activePath}
              openKeys={openKeys}
              toggleOpen={toggleOpen}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

// کامپوننت منوی موبایل (آکاردئونی)
const MobileMenuItem = ({ item, level = 0, activePath, closeMenu }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;
  const isExternal = item.to && (item.to.startsWith('http://') || item.to.startsWith('https://'));
  
  const renderLink = () => {
    const linkStyle = {
      paddingRight: `${level * 20}px`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center'
    };

    if (hasChildren) {
      return (
        <button
          type="button"
          className="mobile-menu-link"
          onClick={() => setIsOpen(open => !open)}
          aria-expanded={isOpen}
          style={linkStyle}
        >
          <span>{item.label}</span>
          <i className={`fa-solid fa-angle-${isOpen ? 'up' : 'down'}`} aria-hidden="true" />
        </button>
      );
    }

    if (!item.to) {
      return (
        <a 
          href="#" 
          className="mobile-menu-link" 
          onClick={closeMenu}
          style={linkStyle}
        >
          <span>{item.label}</span>
        </a>
      );
    }

    if (isExternal) {
      return (
        <a 
          href={item.to} 
          className="mobile-menu-link"
          target="_blank" 
          rel="noopener noreferrer"
          onClick={closeMenu}
          style={linkStyle}
        >
          <span>{item.label}</span>
        </a>
      );
    }

    return (
      <Link 
        to={item.to} 
        className="mobile-menu-link"
        onClick={closeMenu}
        style={linkStyle}
      >
        <span>{item.label}</span>
      </Link>
    );
  };

  return (
    <li className={`mobile-menu-item level-${level}`}>
      {renderLink()}
      {hasChildren && isOpen && (
        <ul className="mobile-sub-menu">
          {item.children.map((child) => (
            <MobileMenuItem
              key={child.id ? `mobile-${child.id}` : `mobile-${child.label}`}
              item={child}
              level={level + 1}
              activePath={activePath}
              closeMenu={closeMenu}
            />
          ))}
        </ul>
      )}
    </li>
  );
};

const NavbarComponent = () => {
  const location = useLocation();
  const navContext = useContext(NavContext);
  const setNav = navContext?.setNav;

  const [isSearchOpen, setSearchOpen] = useState(false);
  const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openKeys, setOpenKeys] = useState([]);
  const [newsCategories, setNewsCategories] = useState([]);
  const [hoverTimer, setHoverTimer] = useState(null);
  const searchInputRef = useRef(null);
  const headerRef = useRef(null);

  // دریافت دسته‌بندی‌های خبر
  useEffect(() => {
    let isMounted = true;
    
    const fetchCategories = async () => {
      try {
        const data = await getCategoryHierarchy(1006);
        if (isMounted && Array.isArray(data)) {
          // تبدیل داده‌ها به فرمت مورد نیاز
          const formattedData = data.map(category => ({
            id: category.id,
            label: category.title,
            to: category.link && category.link.length > 5 ? 
                category.link : `/news/${category.id}`,
            children: category.children ? category.children.map(child => ({
              id: child.id,
              label: child.title,
              to: child.link && child.link.length > 5 ? 
                  child.link : `/news/${child.id}`,
              children: child.children ? child.children.map(child2 => ({
              id: child2.id,
              label: child2.title,
              to: child2.link && child2.link.length > 5 ? 
                  child2.link : `/news/${child2.id}`,
              children: child2.children || [] // بازگشتی
            })) :[]
            })) : []
          }));

          console.log('===========================')
          console.log(formattedData)
          setNewsCategories(formattedData);
        }
      } catch (error) {
        console.error('Error fetching categories:', error);
        if (isMounted) setNewsCategories([]);
      }
    };

    fetchCategories();
    return () => { isMounted = false; };
  }, []);

  // مدیریت hover برای منوهای دسکتاپ
  const handleMouseEnter = (itemKey) => {
    if (hoverTimer) clearTimeout(hoverTimer);
    setOpenKeys(prev => [...prev, itemKey]);
  };

  const handleMouseLeave = (itemKey) => {
    const timer = setTimeout(() => {
      setOpenKeys(prev => prev.filter(key => key !== itemKey));
    }, 300);
    setHoverTimer(timer);
  };

  const toggleOpen = (itemKey) => {
    setOpenKeys(prev => 
      prev.includes(itemKey) 
        ? prev.filter(key => key !== itemKey)
        : [...prev, itemKey]
    );
  };

  // ساخت منوی کامل
  const menuItems = useMemo(() => {
    const items = [
      { label: LABELS.home, to: '/' }
    ];

    // اضافه کردن دسته‌بندی‌های خبر
    if (newsCategories.length > 0) {
      items.push(...newsCategories);
    }

    // اضافه کردن منوهای استاتیک
    items.push(...STATIC_MENU_ITEMS);
    
    return items;
  }, [newsCategories]);

  // تنظیم استایل برای هدر
  useEffect(() => {
    const applyStyles = () => {
      const header = headerRef.current;
      if (!header) return;

      // استایل اصلی هدر
      // header.style.background = 'linear-gradient(90deg, rgba(0,128,96,.30), rgba(5,73,73,.26))';
      // header.style.backdropFilter = 'saturate(140%) blur(12px)';
      // header.style.webkitBackdropFilter = 'saturate(140%) blur(12px)';
      // header.style.borderBottom = '1px solid rgba(255,255,255,.22)';
      // header.style.position = 'sticky';
      // header.style.top = '0';
      // header.style.zIndex = '1000';

      // استایل منوها
      const style = document.createElement('style');
      style.id = 'dynamic-menu-styles';
      style.textContent = `
        /* استایل عمومی منو */
        .desktop-menu {
          display: flex;
          list-style: none;
          margin: 0;
          padding: 0;
        }
        
        .desktop-menu > li {
          position: relative;
        }
        
        .desktop-menu > li > a {
              color: #fff !important;
            text-decoration: none;
            padding: 10px 10px;
            display: block;
            transition: color 0.3s;
            font-weight: 600;
            font-size: 13px;
         
        }
        
        .desktop-menu > li > a:hover,
        .desktop-menu > li.active > a {
          color: #ffb300 !important;
        }
        
        /* زیرمنوها */
        .sub-menu {
          position: absolute;
          top: 100%;
          right: 0;
          min-width: 220px;
          background: rgba(250, 250, 250, 0.95);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          list-style: none;
          padding: 0px 0 0 0;
          margin: 0;
          opacity: 0;
          visibility: hidden;
          transform: translateY(10px);
          transition: all 0.3s ease;
          z-index: 1001;
        }
        
        .has-sub:hover > .sub-menu,
        .has-sub.open > .sub-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }
        
        /* زیرمنوهای سطح دوم و بالاتر */
        .sub-menu .has-sub > .sub-menu {
          top: 0;
          right: 100%;
          margin-right: 2px;
        }
        
        .sub-menu li {
        font-weight:400;
            border-bottom: 1px solid #cdcdcd;
          position: relative;
          color: rgb(5, 73, 73) !important;
        }
        
        .sub-menu a {
          color: rgb(5, 73, 73) !important;
          padding: 12px 20px;
          display: block;
          text-decoration: none;
          transition: all 0.2s;
          white-space: nowrap;
          
            font-weight: 400;
            font-size: 13px;
        }
        
        .sub-menu a:hover {
          // background: rgba(255, 179, 0, 0.1);
          color: #ffb300 !important;
        }
        
        /* آیکون فلش */
        .sub-caret {
          margin-right: 5px;
          transition: transform 0.3s;
        }
        
        .has-sub.open > a .sub-caret {
          transform: rotate(180deg);
        }
        
        /* منوی موبایل */
        .mobile-menu {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        
        .mobile-menu-item {
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        .mobile-menu-link {
          color: white !important;
          text-decoration: none;
          padding: 15px 20px;
          display: block;
          transition: background 0.3s;
        }
        
        .mobile-menu-link:hover {
          background: rgba(255, 255, 255, 0.1);
        }

        button.mobile-menu-link {
          width: 100%;
          border: none;
          background: transparent;
          font: inherit;
          text-align: inherit;
          cursor: pointer;
        }
        
        .mobile-sub-menu {
          list-style: none;
          padding: 0;
          margin: 0;
          background: rgba(0, 0, 0, 0.1);
          border-top: 1px solid rgba(255, 255, 255, 0.05);
        }
        
        /* responsive */
        @media (max-width: 992px) {
          .desktop-menu {
            display: none;
          }
        }
        
        @media (min-width: 993px) {
          .mobile-menu-container {
            display: none;
          }
        }
      `;
      
      // حذف استایل قبلی اگر وجود دارد
      const oldStyle = document.getElementById('dynamic-menu-styles');
      if (oldStyle) oldStyle.remove();
      
      document.head.appendChild(style);
    };

    applyStyles();
  }, []);

  // مدیریت باز و بسته شدن منو
  useEffect(() => {
    if (typeof setNav === 'function') {
      setNav(location.pathname);
    }
  }, [location.pathname, setNav]);

  useEffect(() => {
    if (isSearchOpen || isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isSearchOpen, isMobileMenuOpen]);

  useEffect(() => {
    if (isSearchOpen) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isSearchOpen]);

  // مدیریت اسکرول
  useEffect(() => {
    const handleScroll = () => {
      const header = headerRef.current;
      if (!header) return;
      
      if (window.scrollY > 50) {
        header.classList.add('header--fixed');
      } else {
        header.classList.remove('header--fixed');
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // بستن منوها با ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        if (isSearchOpen) setSearchOpen(false);
        if (isMobileMenuOpen) setMobileMenuOpen(false);
      }
    };
    
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isSearchOpen, isMobileMenuOpen]);

  // مدیریت جستجو
  const handleSearch = (e) => {
    if (e.key === 'Enter') {
      const query = e.target.value.trim();
      if (query) {
        window.location.href = `/news/${encodeURIComponent(query)}`;
        setSearchOpen(false);
      }
    }
  };

  return (
    <>
      <header className="header" ref={headerRef}>
        <div className="container-fluid">
          <div className="row align-items-center">
            {/* لوگو */}
            <div className="col-lg-2 col-6">
              <div className="logo-wrapper">
                <Link to="/">
                  <img 
                    src={logoImage} 
                    alt={TEXTS.logoAlt}
                    className="logo"
                    width="113"
                    height="84"
                  />
                </Link>
              </div>
            </div>

            {/* منوی دسکتاپ */}
            <div className="col-lg-8 d-none d-lg-block">
              <nav className="main-nav">
                <ul className="desktop-menu">
                  {menuItems.map((item) => (
                    <DesktopMenuItem
                      key={item.id ? `desktop-${item.id}` : `desktop-${item.label}`}
                      item={item}
                      activePath={location.pathname}
                      openKeys={openKeys}
                      toggleOpen={toggleOpen}
                      onMouseEnter={handleMouseEnter}
                      onMouseLeave={handleMouseLeave}
                    />
                  ))}
                </ul>
              </nav>
            </div>

            {/* آیکون‌های سمت چپ */}
            <div className="col-lg-2 col-6">
              <div className="header-actions">
                <button
                  className="search-btn d-none d-lg-inline-block"
                  onClick={() => setSearchOpen(true)}
                  aria-label="جستجو"
                >
                  <i className="fa-solid fa-search" />
                </button>
                
                <button
                  className="mobile-toggle d-lg-none"
                  onClick={() => setMobileMenuOpen(true)}
                  aria-label={TEXTS.openMenu}
                  aria-expanded={isMobileMenuOpen}
                >
                  <i className="fa-solid fa-bars" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Overlay جستجو */}
      <div className={`search-overlay ${isSearchOpen ? 'open' : ''}`}>
        <div className="search-container">
          <button
            className="close-search"
            onClick={() => setSearchOpen(false)}
            aria-label={TEXTS.close}
          >
            <i className="fa-solid fa-times" />
          </button>
          
          <div className="search-content">
            <h3>{TEXTS.searchHint}</h3>
            <div className="search-input-wrapper">
              <i className="fa-solid fa-search" />
              <input
                type="text"
                ref={searchInputRef}
                className="search-input"
                placeholder={TEXTS.searchPlaceholder}
                onKeyDown={handleSearch}
              />
            </div>
          </div>
        </div>
      </div>

      {/* منوی موبایل */}
      <div className={`mobile-menu-overlay ${isMobileMenuOpen ? 'open' : ''}`}>
        <div className="mobile-menu-container">
          {/* هدر منوی موبایل */}
          <div className="mobile-menu-header">
            <div className="mobile-logo">
              <img 
                src={logoImage} 
                alt={TEXTS.logoAlt}
                width="40"
                height="40"
              />
              <span>{TEXTS.brand}</span>
            </div>
            
            <button
              className="close-mobile-menu"
              onClick={() => setMobileMenuOpen(false)}
              aria-label={TEXTS.close}
            >
              <i className="fa-solid fa-times" />
            </button>
          </div>

          {/* بخش جستجو در موبایل */}
          <div className="mobile-search">
            <div className="search-input-wrapper">
              <i className="fa-solid fa-search" />
              <input
                type="text"
                className="mobile-search-input"
                placeholder={TEXTS.searchPlaceholder}
                onKeyDown={handleSearch}
              />
            </div>
          </div>

          {/* آیتم‌های منوی موبایل */}
          <ul className="mobile-menu">
            {menuItems.map((item) => (
              <MobileMenuItem
                key={item.id ? `mobile-${item.id}` : `mobile-${item.label}`}
                item={item}
                activePath={location.pathname}
                closeMenu={() => setMobileMenuOpen(false)}
              />
            ))}
          </ul>
        </div>
      </div>

      {/* استایل‌های اضافی */}
      <style jsx>{`
        /* Overlay جستجو */
        .search-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.95);
          z-index: 2000;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .search-overlay.open {
          opacity: 1;
          visibility: visible;
        }
        
        .search-container {
          width: 90%;
          max-width: 600px;
          position: relative;
        }
        
        .close-search {
          position: absolute;
          top: -60px;
          right: 0;
          background: none;
          border: none;
          color: white;
          font-size: 24px;
          cursor: pointer;
        }
        
        .search-content h3 {
          color: white;
          text-align: center;
          margin-bottom: 30px;
        }
        
        .search-input-wrapper {
          position: relative;
        }
        
        .search-input-wrapper i {
          position: absolute;
          right: 20px;
          top: 50%;
          transform: translateY(-50%);
          color: #ccc;
        }
        
        .search-input {
          width: 100%;
          padding: 20px 60px 20px 20px;
          border: 2px solid #ffb300;
          border-radius: 50px;
          background: rgba(255, 255, 255, 0.1);
          color: white;
          font-size: 18px;
        }
        
        /* منوی موبایل */
        .mobile-menu-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.9);
          z-index: 2001;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
        }
        
        .mobile-menu-overlay.open {
          opacity: 1;
          visibility: visible;
        }
        
        .mobile-menu-container {
          position: absolute;
          top: 0;
          right: 0;
          bottom: 0;
          width: 90%;
          max-width: 400px;
          background: linear-gradient(135deg, rgba(0,128,96,0.95), rgba(5,73,73,0.95));
          backdrop-filter: blur(20px);
          overflow-y: auto;
          transform: translateX(100%);
          transition: transform 0.3s ease;
        }
        
        .mobile-menu-overlay.open .mobile-menu-container {
          transform: translateX(0);
        }
        
        .mobile-menu-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .mobile-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          color: white;
          font-weight: bold;
        }
        
        .close-mobile-menu {
          background: none;
          border: none;
          color: white;
          font-size: 24px;
          cursor: pointer;
        }
        
        .mobile-search {
          padding: 20px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.2);
        }
        
        .mobile-search-input {
          width: 100%;
          padding: 12px 20px;
          border: 1px solid rgba(255, 255, 255, 0.3);
          border-radius: 25px;
          background: rgba(255, 255, 255, 0.1);
          color: white;
        }
        
        .mobile-search-input::placeholder {
          color: rgba(255, 255, 255, 0.7);
        }
        
        /* دکمه‌های هدر */
        .header-actions {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 15px;
        }
        
        .search-btn, .mobile-toggle {
          background: none;
          border: none;
          color: white;
          font-size: 20px;
          cursor: pointer;
          padding: 8px;
          border-radius: 50%;
          transition: background 0.3s;
        }
        
        .search-btn:hover, .mobile-toggle:hover {
          background: rgba(255, 255, 255, 0.1);
        }
        
       
      `}</style>
    </>
  );
};

export default NavbarComponent;
